import React from 'react'
import { Formik, Form } from 'formik'
import { Link, useNavigate, Navigate } from 'react-router-dom'
import { LoginApi } from '../../components/login/LoginApi'
import * as Yup from 'yup'
import { useUserStore } from '../../shared/store/userStore'
import { toastError, toastWarning } from '../../shared/toastr'
import { Spinner } from '../../services/Spinner'
import { isTrialPeriod } from '../../shared/utils/isTrialPeriod'
import { enableUserbackReplay, setUserbackUser } from '../../shared/utils/userback'
import { trackUserViaGtm } from '../../shared/utils/googleTagManager'
import { useOfferionTranslation } from '../../shared/store/hooks/useOfferionTranslation'
import { BackgroundBlockPattern } from '../shared/GridBackground'
import { Button } from '../shared/Button'
import { FormInput } from '../shared/form/FormInput'
import { FormField } from '../shared/form/FormField'

export const LoginContainer = () => {
    const { t } = useOfferionTranslation()
    const navigate = useNavigate()
    const user = useUserStore(state => state.user)
    const updateAuthenticatedUser = useUserStore(state => state.updateAuthenticatedUser)

    if (user.authenticated) return <Navigate to="/dashboard" replace />

    const onSubmit = async (values: { email: string; password: string }) => {
        Spinner.show()

        try {
            const result = await LoginApi.login(values.email, values.password)

            trackUserViaGtm(result)
            setUserbackUser(result)

            if (isTrialPeriod(result)) {
                enableUserbackReplay()
            }

            if (result.ShowInvoiceLimitAboutToBeReached == 'True' && result.ShowTenderLimitAboutToBeReached == 'True') {
                const invoiceCount = Number.parseInt(result.NumberOfInvoicesInPackage) - Number.parseInt(result.NumberOfInvoicesCreated)
                const offerCount = Number.parseInt(result.NumberOfTendersInPackage) - Number.parseInt(result.NumberOfTendersCreated)

                const message = t('Allaround.Message.packageThresholdReachedTwofold', {
                    numOffersLeft: offerCount,
                    offerSingular: offerCount == 1 ? t('Allaround.Message.offerAkuzativ') : t('Allaround.Message.offerPlural2'),
                    numInvoicesLeft: invoiceCount,
                    invoiceSingular: invoiceCount == 1 ? t('Allaround.Message.invoiceAkuzativ') : t('Allaround.Message.invoicePlural'),

                    numOffers: result.NumberOfTendersCreated,
                    numOffersInPackage: result.NumberOfTendersInPackage,
                    offerPlural:
                        Number.parseInt(result.NumberOfTendersCreated) == 1
                            ? t('Allaround.Message.offerAkuzativ')
                            : t('Allaround.Message.offerPlural2'),

                    numInvoces: result.NumberOfInvoicesCreated,
                    numInvoicesInPackage: result.NumberOfInvoicesInPackage,
                    invoicePlural:
                        Number.parseInt(result.NumberOfInvoicesCreated) == 1
                            ? t('Allaround.Message.invoiceAkuzativ')
                            : t('Allaround.Message.invoicePlural'),
                })

                toastWarning(message, true, false)
            } else if (result.ShowInvoiceLimitAboutToBeReached == 'True') {
                const count = Number.parseInt(result.NumberOfInvoicesInPackage) - Number.parseInt(result.NumberOfInvoicesCreated)
                const message = t('Allaround.Message.packageThresholdReached', {
                    numOffersOrInvoicesLeft: count,
                    offerOrInvoiceSingular: count == 1 ? t('Allaround.Message.invoiceAkuzativ') : t('Allaround.Message.invoicePlural'),
                    numOffersOrInvoices: result.NumberOfInvoicesCreated,
                    numOffersOrInvoicesInPackage: result.NumberOfInvoicesInPackage,
                })

                toastWarning(message, true, false)
            } else if (result.ShowTenderLimitAboutToBeReached == 'True') {
                const count = Number.parseInt(result.NumberOfTendersInPackage) - Number.parseInt(result.NumberOfTendersCreated)
                const message = t('Allaround.Message.packageThresholdReached', {
                    numOffersOrInvoicesLeft: count,
                    offerOrInvoiceSingular: count == 1 ? t('Allaround.Message.offerAkuzativ') : t('Allaround.Message.offerPlural1'),
                    numOffersOrInvoices: result.NumberOfTendersCreated,
                    numOffersOrInvoicesInPackage: result.NumberOfTendersInPackage,
                })

                toastWarning(message, true, false)
            }

            if (result.ShowInvoiceLimitReached == 'True' && result.ShowTenderLimitReached == 'True') {
                const message = t('Allaround.Message.packageLimitReached', {
                    offersOrInvoicesGenitiv: t('Allaround.Message.offersAndInvoicesGenitiv'),
                })

                toastWarning(message, true, false)
            } else if (result.ShowInvoiceLimitReached == 'True') {
                const message = t('Allaround.Message.packageLimitReached', {
                    offersOrInvoicesGenitiv: t('Allaround.Message.invoicesGenitiv'),
                })

                toastWarning(message, true, false)
            } else if (result.ShowTenderLimitReached == 'True') {
                const message = t('Allaround.Message.packageLimitReached', {
                    offersOrInvoicesGenitiv: t('Allaround.Message.offersGenitiv'),
                })

                toastWarning(message, true, false)
            }

            if (result.ShowPackageAboutToExpireWarning == 'True') {
                const count = Number.parseInt(result.NumberOfDaysTillPackageExpiry)

                const message = t('Allaround.Message.subscriptionExpiresSoon', {
                    numDays: count,
                    days: count == 1 ? t('Allaround.Message.day') : t('Allaround.Message.dayPlural'),
                })

                toastWarning(message, true, false)
            } else if (result.ShowPackageExpiredWarning == 'True') {
                const message = t('Allaround.Message.subscriptionExpired', {
                    packageExpirationDate: result.PackageExpirationDate,
                })

                toastWarning(message, true, false)
            }

            if (result.AnyInvoiceNotFiscalized == 'True') {
                const message = t('Allaround.Message.invoicesNotFiscalized', {
                    link: '/invoices?custom-filter=OnlyNotFiscalized',
                })

                toastWarning(message, true, false)
            }

            updateAuthenticatedUser({ ...result, authenticated: true })

            navigate('/dashboard')
        } catch (error: any) {
            if (error && error.response && error.response.status === 417 && error.response.data === 'invalid_grant')
                toastError('Login.popupErrorWrongUser')
            else toastError('Allaround.Message.unknownError')
        } finally {
            Spinner.hide()
        }
    }

    const schema = Yup.object().shape({
        email: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('Login.email') })),
        password: Yup.string()
            .required(t('Allaround.Validation.required', { fieldName: t('Login.password') }))
            .min(6, t('Allaround.Validation.minLength', { fieldName: t('Login.password'), minLength: 6 })),
    })

    return (
        <div className="v2-root">
            <main className="w-full h-full pt-12 sm:pt-16 md:pt-32">
                <div className="w-full h-fit px-5 md:bg-grey10 flex flex-col gap-8 max-w-[430px] mx-auto md:p-10 rounded-[14px] md:border md:border-border">
                    <div className="flex flex-col gap-3 md:items-center">
                        <h1 className="text-xl text-grey100">{t('Login.pageTitle')}</h1>
                        <div className="flex items-baseline gap-1">
                            <span className="text-base text-grey80">{t('Login.noAccount')}</span>

                            <Link to="/register" className="text-base-bold text-blue40">
                                {t('Login.register')}
                            </Link>
                        </div>
                    </div>
                    <Formik validationSchema={schema} initialValues={{ email: '', password: '' }} onSubmit={onSubmit}>
                        {formikProps => {
                            return (
                                <Form noValidate={true} autoComplete="false">
                                    <div className="w-full flex flex-col gap-2.5 mb-8">
                                        <FormField
                                            component={FormInput}
                                            name="email"
                                            type="email"
                                            label={t('Login.email')}
                                            placeholder={t('Login.emailPlaceholder')}
                                        />

                                        <FormField
                                            component={FormInput}
                                            name="password"
                                            type="password"
                                            label={t('Login.password')}
                                            placeholder={t('Login.passwordPlaceholder')}
                                        />

                                        <Link to="/forgot-password" className="text-sm text-grey80">
                                            {t('Login.forgotPassword')}
                                        </Link>
                                    </div>

                                    <Button type="submit" disabled={formikProps.isSubmitting} loading={formikProps.isSubmitting}>
                                        {t('Login.loginButton')}
                                    </Button>
                                </Form>
                            )
                        }}
                    </Formik>
                </div>
                <BackgroundBlockPattern />
            </main>
        </div>
    )
}
